
import React from "react";
import { useFormContext } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { AddressAutocomplete } from "@/components/ui/address-autocomplete";
import { US_STATES } from '@/utils/statesList';
import LocationPickerButton from "@/components/ui/location-picker-button";
import { AssociationFormValues } from "@/types/association";

interface DefaultPropertyAddressTabProps {
  handleDefaultPropertyAddressSelect: (address: { 
    fullAddress: string; 
    city?: string;
    state?: string;
    zipCode?: string;
    lat?: number;
    lon?: number;
  }) => void;
  handleLocationSelect: (latitude: number, longitude: number) => void;
}

export const DefaultPropertyAddressTab: React.FC<DefaultPropertyAddressTabProps> = ({ 
  handleDefaultPropertyAddressSelect,
  handleLocationSelect 
}) => {
  const { control, watch } = useFormContext<AssociationFormValues>();
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Default Property Address</CardTitle>
        <CardDescription>
          Default property address for this association
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField
          control={control}
          name="default_property_address"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Street Address</FormLabel>
              <FormControl>
                <AddressAutocomplete 
                  {...field}
                  onAddressSelect={handleDefaultPropertyAddressSelect}
                />
              </FormControl>
              <FormDescription>
                The default address for any new properties in this association
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField
            control={control}
            name="default_property_city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>City</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={control}
            name="default_property_state"
            render={({ field }) => (
              <FormItem>
                <FormLabel>State</FormLabel>
                <Select
                  value={field.value || ""}
                  onValueChange={field.onChange}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {US_STATES.map((state) => (
                      <SelectItem key={state.value} value={state.value}>
                        {state.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={control}
            name="default_property_zip"
            render={({ field }) => (
              <FormItem>
                <FormLabel>ZIP Code</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <div className="space-y-2">
            <FormField
              control={control}
              name="default_property_latitude"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Latitude</FormLabel>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Input 
                        type="number" 
                        step="0.000001"
                        value={field.value !== null ? field.value : ''} 
                        onChange={e => field.onChange(e.target.value ? parseFloat(e.target.value) : null)} 
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          <div className="space-y-2">
            <FormField
              control={control}
              name="default_property_longitude"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Longitude</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      step="0.000001"
                      value={field.value !== null ? field.value : ''} 
                      onChange={e => field.onChange(e.target.value ? parseFloat(e.target.value) : null)} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        
        <div className="pt-2">
          <LocationPickerButton
            latitude={watch("default_property_latitude") || undefined}
            longitude={watch("default_property_longitude") || undefined}
            onLocationSelect={handleLocationSelect}
          />
        </div>
      </CardContent>
    </Card>
  );
};
